import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

const OLLAMA_CLOUD_ENDPOINT = 'https://ollama.com/api/chat';
const DEFAULT_MODEL = 'gemma4:31b';

/**
 * Carte « Ollama Cloud » du Super Admin → Paramètres → Clés API IA.
 *
 * La clé est stockée côté plateforme et n'est JAMAIS envoyée au navigateur :
 * la fonction backend `ollama-cloud` relaie les requêtes vers
 * https://ollama.com avec le header Authorization: Bearer <clé>.
 *
 * Le bouton « Tester la connexion » envoie une vraie requête et affiche
 * la vraie réponse renvoyée par le modèle.
 */
interface OllamaCloudSettingsProps {
  /** Vrai si Ollama Cloud est le fournisseur IA actif de la plateforme. */
  active?: boolean;
  activating?: boolean;
  onActivate?: () => void;
}

export default function OllamaCloudSettings({ active = false, activating = false, onActivate }: OllamaCloudSettingsProps) {
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(DEFAULT_MODEL);
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testReply, setTestReply] = useState<string | null>(null);
  const [testModel, setTestModel] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('zifek')
      .select('ollama_cloud_api_key, ollama_cloud_model')
      .order('id', { ascending: true })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        const row = data as { ollama_cloud_api_key?: string | null; ollama_cloud_model?: string | null } | null;
        if (row) {
          setApiKey(String(row.ollama_cloud_api_key || ''));
          setModel(String(row.ollama_cloud_model || DEFAULT_MODEL));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const { error: keyError } = await supabase.rpc('save_zifek_api_key', {
        key_column: 'ollama_cloud_api_key',
        key_value: apiKey.trim(),
      });
      if (keyError) throw keyError;

      const { error: modelError } = await supabase.rpc('save_zifek_api_key', {
        key_column: 'ollama_cloud_model',
        key_value: model.trim() || DEFAULT_MODEL,
      });
      if (modelError) throw modelError;

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setError(null);
    setTestReply(null);
    setTestModel(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('ollama-cloud', {
        body: {
          model: model.trim() || DEFAULT_MODEL,
          message: 'Réponds en une phrase courte en français pour confirmer que la connexion Ollama Cloud fonctionne.',
        },
      });

      if (fnError) {
        let msg = fnError.message || 'Test impossible.';
        try {
          const ctx = (fnError as unknown as { context?: Response }).context;
          if (ctx) {
            const body = (await ctx.json()) as { error?: string };
            if (body?.error) msg = body.error;
          }
        } catch {
          /* message générique conservé */
        }
        throw new Error(msg);
      }

      const payload = data as { reply?: string; error?: string; model?: string } | null;
      if (payload?.error) throw new Error(payload.error);
      if (!payload?.reply) throw new Error('Réponse vide reçue depuis Ollama Cloud.');
      setTestReply(payload.reply);
      setTestModel(payload.model || model.trim() || DEFAULT_MODEL);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Test impossible.');
    } finally {
      setTesting(false);
    }
  };

  const configured = Boolean(apiKey.trim());

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center flex-shrink-0">
          <i className="ri-cloud-line text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-foreground-900">Ollama Cloud</h3>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 font-medium">
                Modèles ouverts
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                configured ? 'bg-green-100 text-green-700' : 'bg-background-100 text-foreground-500'
              }`}>
                {configured ? 'Configuré' : 'Non configuré'}
              </span>
              {active && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-accent-500 text-background-50 dark:text-foreground-950 font-semibold flex items-center gap-1 whitespace-nowrap">
                  <i className="ri-checkbox-circle-fill"></i>
                  Actif partout
                </span>
              )}
            </div>
            {onActivate && (
              <button
                type="button"
                onClick={onActivate}
                disabled={active || activating || !configured}
                title={!configured ? "Enregistrez d'abord votre clé Ollama Cloud" : undefined}
                className={`h-8 px-3 rounded-full text-[11px] font-semibold whitespace-nowrap transition-colors flex items-center gap-1.5 flex-shrink-0 ${
                  active
                    ? 'bg-accent-100 text-accent-900 cursor-default'
                    : 'bg-foreground-950 text-background-50 hover:bg-foreground-800 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed'
                }`}
              >
                {activating ? (
                  <><i className="ri-loader-4-line animate-spin"></i>Activation…</>
                ) : active ? (
                  <><i className="ri-check-line"></i>Activé</>
                ) : (
                  <><i className="ri-flashlight-line"></i>Activer partout</>
                )}
              </button>
            )}
          </div>
          <p className="text-xs text-foreground-500 mb-4">
            Appelez les modèles ouverts d&apos;Ollama depuis le backend Zifek. La clé reste côté serveur
            et n&apos;est jamais exposée au navigateur.
          </p>

          <div className="p-3 rounded-lg bg-background-100 mb-4">
            <p className="text-[11px] font-semibold text-foreground-600 mb-1">Endpoint (côté serveur)</p>
            <div className="flex items-center gap-2 flex-wrap">
              <code className="text-[11px] text-foreground-800 font-mono break-all">{OLLAMA_CLOUD_ENDPOINT}</code>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 font-medium whitespace-nowrap">
                POST · JSON
              </span>
            </div>
            <p className="text-[10px] text-foreground-400 mt-1.5">
              Authentification : <span className="font-mono">Authorization: Bearer &lt;clé&gt;</span> ·
              Relais sécurisé par la fonction <span className="font-mono">ollama-cloud</span>.
            </p>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Clé API Ollama Cloud</label>
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <input
                    type={showKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    placeholder="ollama-..."
                    autoComplete="off"
                    className="w-full h-10 px-3 pr-10 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKey((v) => !v)}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 cursor-pointer"
                    title={showKey ? 'Cacher' : 'Afficher'}
                  >
                    <i className={`text-sm ${showKey ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving || loading}
                  className="h-10 px-4 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 bg-foreground-950 text-background-50 hover:bg-foreground-800 flex items-center gap-1.5"
                >
                  {saving ? <><i className="ri-loader-4-line animate-spin text-xs"></i>...</> : saved ? <><i className="ri-check-line text-xs"></i>OK</> : 'Enregistrer'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Modèle</label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                  placeholder={DEFAULT_MODEL}
                  className="flex-1 h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 font-mono"
                />
                <button
                  type="button"
                  onClick={testConnection}
                  disabled={testing}
                  className="h-10 px-4 rounded-full text-xs font-semibold whitespace-nowrap cursor-pointer border border-primary-300 text-primary-700 bg-primary-50 hover:bg-primary-100 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {testing ? <><i className="ri-loader-4-line animate-spin"></i>Test…</> : <><i className="ri-plug-line"></i>Tester la connexion</>}
                </button>
              </div>
              <p className="text-[10px] text-foreground-400 mt-1.5">
                Modèle par défaut : <span className="font-mono">{DEFAULT_MODEL}</span> (inclus dans l&apos;offre gratuite Ollama Cloud).
              </p>
            </div>
          </div>

          {testReply && (
            <div className="mt-4 p-3 rounded-lg bg-green-50 border border-green-100">
              <p className="text-[11px] font-semibold text-green-700 mb-1 flex items-center gap-1.5">
                <i className="ri-checkbox-circle-line"></i>
                Réponse réelle reçue {testModel ? <span className="font-mono font-normal text-green-600">({testModel})</span> : null}
              </p>
              <p className="text-xs text-foreground-700 whitespace-pre-wrap leading-relaxed">{testReply}</p>
            </div>
          )}

          {error && (
            <p className="text-xs text-red-600 mt-3 flex items-start gap-1.5">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0"></i>{error}
            </p>
          )}

          <div className="flex items-center gap-2 mt-3">
            <a
              href="https://docs.ollama.com/api/introduction"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-foreground-400 hover:text-primary-500 transition-colors flex items-center gap-1"
            >
              <i className="ri-external-link-line"></i>
              Documentation
            </a>
            <span className="text-[10px] text-foreground-300">|</span>
            <a
              href="https://ollama.com/api/tags"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-foreground-400 hover:text-primary-500 transition-colors"
            >
              Catalogue des modèles
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}